import { Moon, Sun } from '@tamagui/lucide-icons';
import { SizableText, XStack } from 'tamagui';
import { useThemeStore } from '../lib/theme-store';
import { useFocusRing } from '../lib/focus-ring';

const TAP_TARGET = 44;

/**
 * Light/dark switch. `variant="menu"` renders a full-width row for the account
 * overflow menu; `variant="inline"` renders a self-contained pill button for
 * the Settings page. Both share the same store-backed toggle.
 */
export function ThemeToggle({ variant = 'inline' }: { variant?: 'menu' | 'inline' }) {
  const mode = useThemeStore((s) => s.mode);
  const toggle = useThemeStore((s) => s.toggle);
  const isDark = mode === 'dark';
  const Icon = isDark ? Sun : Moon;
  const label = isDark ? 'Switch to light mode' : 'Switch to dark mode';
  const ring = useFocusRing();

  if (variant === 'menu') {
    return (
      <XStack
        tag="button"
        role="menuitem"
        alignItems="center"
        gap="$3"
        width="100%"
        height={TAP_TARGET}
        paddingHorizontal="$3"
        backgroundColor="transparent"
        borderWidth={0}
        cursor="pointer"
        hoverStyle={{ backgroundColor: '$backgroundHover' }}
        pressStyle={{ backgroundColor: '$backgroundPress' }}
        onPress={toggle}
        aria-label={label}
        {...ring}
      >
        <Icon size={18} color="$color10" />
        <SizableText size="$3" color="$color11">
          {isDark ? 'Light mode' : 'Dark mode'}
        </SizableText>
      </XStack>
    );
  }

  return (
    <XStack
      tag="button"
      alignItems="center"
      gap="$2"
      height={TAP_TARGET}
      paddingHorizontal="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
      backgroundColor="$color2"
      cursor="pointer"
      hoverStyle={{ backgroundColor: '$backgroundHover' }}
      pressStyle={{ backgroundColor: '$backgroundPress' }}
      onPress={toggle}
      aria-label={label}
      {...ring}
    >
      <Icon size={18} color="$accent" />
      <SizableText size="$3" color="$color11">
        {isDark ? 'Light mode' : 'Dark mode'}
      </SizableText>
    </XStack>
  );
}
